import { ui } from '../../ui/app/store';
import { type HudAction, type HudView, clockText } from './env';
import { instUi, runAction } from './hudStore';

const confirm = (text: string, ok: () => void): void => {
  ui.confirm.value = { text, ok, okLabel: 'Yes' };
};

function Buttons({ actions, small }: { actions: HudAction[]; small?: boolean }) {
  return (
    <>
      {actions.map((a) => (
        <button
          type="button"
          key={a.id}
          class={`btn${small ? ' sm' : ''}${a.tone === 'gold' ? ' btn--gold' : ''}${a.tone === 'danger' ? ' ih-danger' : ''}`}
          disabled={a.disabled}
          onClick={() => runAction(a, confirm)}
        >
          {a.label}
        </button>
      ))}
    </>
  );
}

/** Purification orb (Rift Delve): a ring that fills clockwise, with the portal marks. */
function GaugeOrb({ pct, marks }: { pct: number; marks: number[] }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, pct));
  return (
    <div class="ih-orb" role="meter" aria-label="Purification" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(v)}>
      <svg viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} class="ih-orb-track" />
        <circle cx="32" cy="32" r={r} class="ih-orb-fill" style={{ strokeDasharray: `${(v / 100) * c} ${c}` }} transform="rotate(-90 32 32)" />
        {marks.map((m) => {
          const ang = (m / 100) * Math.PI * 2 - Math.PI / 2;
          return <line key={m} x1={32 + Math.cos(ang) * 21} y1={32 + Math.sin(ang) * 21} x2={32 + Math.cos(ang) * 31} y2={32 + Math.sin(ang) * 31} class={`ih-orb-mark${v >= m ? ' on' : ''}`} />;
        })}
      </svg>
      <span class="ih-orb-num">
        {Math.floor(v)}
        <small>%</small>
      </span>
    </div>
  );
}

function Revives({ n, max, label }: { n: number; max: number; label: string }) {
  return (
    <div class="ih-revives" title={`${label}: ${n} of ${max}`}>
      <span class="ih-lbl">{label}</span>
      <span class="ih-crosses">
        {Array.from({ length: Math.max(max, n) }, (_, i) => (
          <i key={i} class={i < n ? 'on' : ''} />
        ))}
      </span>
    </div>
  );
}

function Scoreboard({ v }: { v: HudView }) {
  const s = v.score;
  if (!s) return null;
  return (
    <div class="ih-score" aria-label={`Crimson ${s.red}, Azure ${s.blue}`}>
      <div class={`ih-side red${s.mine === 'red' ? ' mine' : ''}`}>
        <span class="ih-side-name">Crimson</span>
        <span class="ih-side-pts">
          {Array.from({ length: 3 }, (_, i) => (
            <i key={i} class={i < s.red ? 'on' : ''} />
          ))}
        </span>
        <b>{s.red}</b>
      </div>
      <div class="ih-round">
        <span>Round {s.round}</span>
        {v.timer ? <b class={v.timer.warn ? 'warn' : ''}>{clockText(v.timer.seconds)}</b> : <b>—</b>}
      </div>
      <div class={`ih-side blue${s.mine === 'blue' ? ' mine' : ''}`}>
        <b>{s.blue}</b>
        <span class="ih-side-pts">
          {Array.from({ length: 3 }, (_, i) => (
            <i key={i} class={i < s.blue ? 'on' : ''} />
          ))}
        </span>
        <span class="ih-side-name">Azure</span>
      </div>
    </div>
  );
}

/** Instance HUD: timer, Rift gauge, revives, gate progress, Crucible score. */
export function InstanceHud() {
  const v = instUi.hud.value;
  if (!v || ui.screen.value !== 'game') return null;
  return (
    <>
      {v.score && <Scoreboard v={v} />}
      {v.banner && <div class="ih-banner">{v.banner}</div>}
      <div class={`ih interactive ih--${v.kind}`} onPointerDown={(e) => e.stopPropagation()}>
        <div class="ih-head">
          <div class="ih-titles">
            <div class="ih-title">{v.title}</div>
            <div class="ih-sub">{v.subtitle}</div>
          </div>
          {v.timer && !v.score && (
            <div class={`ih-timer${v.timer.warn ? ' warn' : ''}`}>
              <span>{v.timer.label}</span>
              <b>{clockText(v.timer.seconds)}</b>
            </div>
          )}
        </div>
        {(v.gauge !== null || v.revives || v.pips.length > 0) && (
          <div class="ih-mid">
            {v.gauge !== null && <GaugeOrb pct={v.gauge} marks={v.marks} />}
            {v.gauge !== null && (
              <div class="ih-gauge-bar" aria-hidden="true">
                <i style={{ width: `${Math.min(100, v.gauge)}%` }} />
                {v.marks.map((m) => (
                  <b key={m} style={{ left: `${m}%` }} class={v.gauge !== null && v.gauge >= m ? 'on' : ''} />
                ))}
              </div>
            )}
            {v.pips.length > 0 && (
              <div class="ih-pips">
                {v.pips.map((p) => (
                  <span key={p.label} class={`ih-pip ${p.state}`}>
                    {p.label}
                  </span>
                ))}
              </div>
            )}
            {v.revives && <Revives n={v.revives.n} max={v.revives.max} label={v.revives.label} />}
          </div>
        )}
        {v.badges.length > 0 && (
          <div class="ih-badges">
            {v.badges.map((b) => (
              <span key={b.text} class={`inst-chip ${b.tone}`}>
                {b.text}
              </span>
            ))}
          </div>
        )}
        {v.actions.length > 0 && (
          <div class="ih-actions">
            <Buttons actions={v.actions} small />
          </div>
        )}
      </div>
    </>
  );
}

/** Instance death screen (replaces the default one inside instances). */
export function InstanceDeath() {
  const d = instUi.death.value;
  if (!d || ui.screen.value !== 'game') return null;
  return (
    <div class="death panel interactive ih-death" onPointerDown={(e) => e.stopPropagation()}>
      <h2>{d.title}</h2>
      {d.countdown !== null && <div class="t">{Math.ceil(d.countdown)}</div>}
      <div class="ih-death-text">{d.text}</div>
      <div class="opts">
        <Buttons actions={d.actions} />
      </div>
    </div>
  );
}

/** End-of-run card (failed hunts, Crucible results). */
export function InstanceResult() {
  const r = instUi.result.value;
  if (!r || ui.screen.value !== 'game' || ui.rewards.value) return null;
  return (
    <div class={`panel interactive ih-result ${r.tone}`} onPointerDown={(e) => e.stopPropagation()}>
      <div class="panel__title">{r.subtitle}</div>
      <div class="ih-result-body">
        <h3>{r.title}</h3>
        {r.lines.map((l) => (
          <p key={l}>{l}</p>
        ))}
        <div class="ih-result-actions">
          <Buttons actions={r.actions} />
        </div>
      </div>
    </div>
  );
}
